import { type Component, createMemo, createSignal, createEffect, onCleanup, For, Show } from 'solid-js';
import { gameStore } from '@/stores/gameStore';
import { useTranslation } from '@/i18n';

/**
 * TeamsProgress - Shows simplified progress of all teams
 *
 * According to requirements:
 * - Teams can ONLY see number of solved puzzles of other teams
 * - No points, no positions, no detailed ranking
 * - Refreshes according to ranking_refresh_interval (default 15min)
 * - Blackout before end (ranking_blackout_before_end, default 30min)
 */
const TeamsProgress: Component = () => {
  const { t } = useTranslation();

  // Lightbox state for viewing team photos fullscreen
  const [lightboxPhoto, setLightboxPhoto] = createSignal<{ url: string; teamName: string } | null>(null);

  // Track next ranking update from store (reactive)
  const nextRankingUpdateFromStore = createMemo(() => gameStore.gameState.nextRankingUpdate || 0);

  // Local countdown that decrements every second
  const [localCountdown, setLocalCountdown] = createSignal(0);

  // Sync with store when it changes
  createEffect(() => {
    const storeValue = nextRankingUpdateFromStore();
    console.log('[TeamsProgress] Store nextRankingUpdate:', storeValue);
    if (storeValue > 0) {
      setLocalCountdown(storeValue);
    }
  });

  // Countdown timer interval - decrements every second
  createEffect(() => {
    const interval = setInterval(() => {
      setLocalCountdown(prev => {
        if (prev > 0) return prev - 1;
        // When reaches 0, sync with store again
        const storeVal = nextRankingUpdateFromStore();
        return storeVal > 0 ? storeVal : 0;
      });
    }, 1000);

    onCleanup(() => clearInterval(interval));
  });

  // Use local countdown for display
  const countdown = localCountdown;

  // Format countdown as MM:SS
  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Check if team has finished (blocking ranking view)
  const isTeamFinished = createMemo(() => {
    if (gameStore.gameState.teamFinished) return true;
    if (gameStore.gameState.participation?.is_finished) return true;
    if (gameStore.gameState.team?.is_finished) return true;
    return false;
  });

  // Check if ranking is frozen
  const isRankingFrozen = createMemo(() => gameStore.gameState.rankingFrozen);

  // Get current team ID
  const currentTeamId = createMemo(() => gameStore.gameState.team?.id);
  const currentTeamName = createMemo(() => gameStore.gameState.team?.name);

  // Get total answers count from template
  const totalAnswers = createMemo(() => gameStore.gameState.answers?.length || 0);

  // Get teams progress from ranking data
  // The ranking data is refreshed according to server settings
  const teamsProgress = createMemo(() => {
    const ranking = gameStore.gameState.ranking || [];
    const myTeamId = currentTeamId();
    const myTeamName = currentTeamName();

    return ranking
      .map(team => ({
        id: team.team_id,
        name: team.team_name,
        solvedCount: team.correct_answers || 0,
        isCurrentTeam: team.team_id === myTeamId || team.team_name === myTeamName,
        photoUrl: team.photo_url || null,
      }))
      .sort((a, b) => {
        // Current team first, then by solved count
        if (a.isCurrentTeam) return -1;
        if (b.isCurrentTeam) return 1;
        return b.solvedCount - a.solvedCount;
      });
  });

  // Calculate own team progress from actual progress data
  const ownTeamSolved = createMemo(() => {
    const progress = gameStore.gameState.progress || [];
    return progress.filter(p => p.status === 'correct').length;
  });

  // Check if there are other teams
  const hasOtherTeams = createMemo(() =>
    teamsProgress().filter(t => !t.isCurrentTeam).length > 0
  );

  return (
    <div class="space-y-4">
      {/* Header */}
      <h3 class="text-lg font-semibold text-white/90">
        {t('teamsProgress.title')}
      </h3>

      {/* BLOCKED VIEW - Team finished, show blocking message */}
      <Show when={isTeamFinished()}>
        <div class="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-2xl p-8 border border-amber-500/30 text-center">
          <div class="text-6xl mb-4 animate-bounce">🏁</div>
          <h3 class="text-2xl font-bold text-amber-400 mb-3">
            {t('teamsProgress.congratulations')}
          </h3>
          <p class="text-white/80 text-lg mb-2">
            {t('teamsProgress.youFinished')}
          </p>
          <div class="bg-black/30 rounded-xl p-4 mt-4">
            <p class="text-emerald-300/90 font-medium">
              🎯 {t('teamsProgress.resultsAtFinish')}
            </p>
            <p class="text-white/50 text-sm mt-2">
              {t('teamsProgress.rankingHidden')}
            </p>
          </div>
        </div>
      </Show>

      {/* NORMAL VIEW - Only show if team hasn't finished */}
      <Show when={!isTeamFinished()}>
        {/* Frozen ranking alert */}
        <Show when={isRankingFrozen()}>
          <div class="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-xl p-4 border border-blue-500/30 mb-4">
            <div class="flex items-center gap-3">
              <div class="text-3xl">🧊</div>
              <div>
                <p class="text-blue-300 font-semibold">{t('teamsProgress.rankingFrozen')}</p>
                <p class="text-white/60 text-sm">
                  {t('teamsProgress.rankingFrozenDesc', { minutes: gameStore.gameState.rankingFreezeTime })}
                </p>
              </div>
            </div>
          </div>
        </Show>

        {/* Teams list */}
        <div class="space-y-2">
        {/* Current team - always show with accurate data */}
        <div class="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-xl p-4 border border-emerald-500/30">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-emerald-500/30 flex items-center justify-center">
                <span class="text-lg">👤</span>
              </div>
              <div>
                <div class="font-semibold text-white">
                  {currentTeamName() || t('teamsProgress.yourTeam')}
                </div>
                <div class="text-xs text-emerald-400">
                  {t('teamsProgress.yourTeam')}
                </div>
              </div>
            </div>
            <div class="text-right">
              <div class="text-2xl font-bold text-emerald-400">
                {ownTeamSolved()}/{totalAnswers()}
              </div>
              <div class="text-xs text-white/50">
                {t('teamsProgress.solvedCount')}
              </div>
            </div>
          </div>

          {/* Progress bar for current team */}
          <div class="mt-3 h-2 bg-black/30 rounded-full overflow-hidden">
            <div
              class="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 transition-all duration-500"
              style={{ width: `${totalAnswers() > 0 ? (ownTeamSolved() / totalAnswers()) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Separator */}
        <Show when={hasOtherTeams()}>
          <div class="flex items-center gap-2 py-2">
            <div class="flex-1 h-px bg-white/10" />
            <span class="text-xs text-white/30">{t('teamsProgress.otherTeams')}</span>
            <div class="flex-1 h-px bg-white/10" />
          </div>
        </Show>

        {/* Other teams */}
        <For
          each={teamsProgress().filter(t => !t.isCurrentTeam)}
          fallback={
            <div class="text-center py-8 text-white/40">
              {t('teamsProgress.noTeams')}
            </div>
          }
        >
          {(team) => (
            <div class="bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors overflow-hidden">
              <div class="p-4">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                      <span class="text-lg">👥</span>
                    </div>
                    <div class="font-medium text-white/80">
                      {team.name}
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="text-xl font-bold text-white/70">
                      {team.solvedCount}/{totalAnswers()}
                    </div>
                    <div class="text-xs text-white/40">
                      {t('teamsProgress.solvedCount')}
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div class="mt-3 h-1.5 bg-black/30 rounded-full overflow-hidden">
                  <div
                    class="h-full bg-white/30 transition-all duration-500"
                    style={{ width: `${totalAnswers() > 0 ? (team.solvedCount / totalAnswers()) * 100 : 0}%` }}
                  />
                </div>
              </div>

              {/* Team photo - shown below the progress bar, clickable for fullscreen */}
              <Show when={team.photoUrl}>
                <div
                  class="border-t border-white/10 cursor-pointer relative group"
                  onClick={() => setLightboxPhoto({ url: team.photoUrl!, teamName: team.name })}
                >
                  <img
                    src={team.photoUrl!}
                    alt={t('teamsProgress.teamPhotoAlt', { name: team.name })}
                    class="w-full h-32 object-cover transition-transform group-hover:scale-[1.02]"
                    loading="lazy"
                  />
                  {/* Expand hint */}
                  <div class="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <span class="text-white/0 group-hover:text-white/90 text-2xl transition-colors">🔍</span>
                  </div>
                </div>
              </Show>
            </div>
          )}
        </For>
      </div>

        {/* Info about refresh */}
        <div class="text-center mt-4">
          <Show when={!isRankingFrozen()}>
            <Show when={countdown() > 0}>
              <div class="bg-white/5 rounded-lg p-3 inline-block">
                <p class="text-xs text-white/50 mb-1">
                  {t('teamsProgress.nextRefreshIn')}
                </p>
                <p class="text-lg font-mono font-bold text-emerald-400">
                  {formatCountdown(countdown())}
                </p>
              </div>
            </Show>
            <Show when={countdown() === 0}>
              <p class="text-xs text-white/30">
                {t('teamsProgress.dataRefreshedEvery', { minutes: gameStore.gameState.rankingUpdateFrequency })}
              </p>
            </Show>
          </Show>
          <Show when={isRankingFrozen()}>
            <p class="text-xs text-blue-300/50">
              🧊 {t('teamsProgress.rankingFrozenUntilEnd')}
            </p>
          </Show>
        </div>
      </Show>

      {/* Photo Lightbox Modal */}
      <Show when={lightboxPhoto()}>
        <div
          class="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          {/* Close button */}
          <button
            class="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white text-2xl hover:bg-white/20 transition-colors"
            onClick={() => setLightboxPhoto(null)}
          >
            ✕
          </button>

          {/* Team name */}
          <div class="absolute top-4 left-4 bg-black/50 rounded-lg px-4 py-2">
            <p class="text-white font-semibold">{lightboxPhoto()!.teamName}</p>
          </div>

          {/* Full size image */}
          <img
            src={lightboxPhoto()!.url}
            alt={t('teamsProgress.teamPhotoAlt', { name: lightboxPhoto()!.teamName })}
            class="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Hint to close */}
          <p class="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-sm">
            {t('teamsProgress.tapToClose')}
          </p>
        </div>
      </Show>
    </div>
  );
};

export default TeamsProgress;
